import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  useEditMode,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { RichTextContent } from './shared/RichTextContent';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * HawkFlock — the property's resident VIP program.
 *
 * Layout:
 *   1. Hawk Flock logo (editable image token — editor picks from property media)
 *   2. Six-photo gallery (one image slot group per tile, each pickable)
 *   3. Participating partners as a vertical row list — business name on the
 *      left, offer(s) always on the right. Two-column layout at every
 *      viewport, so sparse entries like SaladWorks and dense entries like
 *      AdventHealth both read as "name | offer".
 *   4. Footnote
 *
 * The partners list is one richtext token so the office can add / remove
 * businesses from the editor without touching code.
 */

/**
 * Logo slot group — same shape as the parent theme's Header logo
 * (image + alt), which is the proven pattern that drives the editor's
 * media picker correctly. The alt-text field is intentionally sibling
 * to the image so the editor can edit both from one panel; a direct
 * image token wasn't surfacing the picker properly in this slot. The
 * lint rule `slot-group-missing-source-binding` fires on this because
 * logo alt text should NOT auto-swap with the picked image — it stays
 * "Hawk Flock" regardless of which logo variant is chosen. Treated as
 * a known false-positive, matching the parent Header's logo slot.
 */
const logoDef: SlotGroupDefinition = {
  label: 'Hawk Flock Logo',
  description:
    'Logo displayed at the top of the section. Pick the Hawk Flock logo from the property media library.',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_primary_logo',
      suggestedFields: [
        'property_primary_logo',
        'property_mono_black_logo',
        'property_secondary_logo',
      ],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Hawk Flock — The Ultimate VIP Program',
    },
  },
};

/**
 * Six individually pickable gallery photos. Each is its own slot group so the
 * editor gets a media picker per tile; defaults point at Community photos.
 * Kept as inline literals because contentSchema is statically extracted.
 */
const photo1Def: SlotGroupDefinition = {
  label: 'Gallery Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[10].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[10].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 10, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Gallery Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[11].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[11].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 11, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Gallery Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[12].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[12].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 12, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Gallery Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[13].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[13].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 13, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo5Def: SlotGroupDefinition = {
  label: 'Gallery Photo 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[14].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[14].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 14, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo6Def: SlotGroupDefinition = {
  label: 'Gallery Photo 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[15].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[15].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 15, fieldMappings: { image: 'image', alt: 'alt_text' } },
};
const DEFAULT_PARTNERS_HTML =
  '<ul>' +
    '<li><strong>AdventHealth Sports Park at Bluhawk</strong>' +
      '<p>Complimentary Public Ice Skating with shoe rental (valid for up to 4 guests).<br>' +
      '<em>*Check the public calendar first for availability at bluhawksports.com/public-ice-skating</em></p>' +
      '<p>Complimentary Open Fieldhouse access: play pickleball, soccer, volleyball &amp; basketball.<br>' +
      '<em>*Check the fieldhouse calendar first for availability at bluhawksports.com/fieldhouse</em></p>' +
      '<p>Spend $25 in arcade credits, receive $25 free arcade credits.</p>' +
    '</li>' +
    '<li><strong>C26 Hub</strong>' +
      '<p><a href="/c26">Click here for a full list of resident perks.</a></p>' +
    '</li>' +
    '<li><strong>LevelUp</strong>' +
      '<p>50% off gaming when you present your Hawk Flock Card.<br>' +
      '<em>*Restrictions may apply during other event promotions, tournaments, and events.</em></p>' +
    '</li>' +
    '<li><strong>Scotts Diamond Designs</strong>' +
      '<p>Clean and inspect rings purchased from any store for FREE.</p>' +
    '</li>' +
    '<li><strong>Brutus Barkery + Bath</strong>' +
      '<p>(Formerly Three Dog Bakery) — 10% off store purchases.<br>' +
      '<em>*Excludes food.</em></p>' +
    '</li>' +
    '<li><strong>Jinya</strong>' +
      '<p>10% discount on food and non-alcoholic beverages for residents. T&amp;C may apply.<br>' +
      '<em>*Must present valid Hawk Flock VIP card.<br>' +
      '*Dine-in only.<br>' +
      '*Not applicable for alcoholic beverages.<br>' +
      '*Not applicable during Happy Hour.<br>' +
      '*Cannot be combined with any other promotions.</em></p>' +
    '</li>' +
    '<li><strong>SaladWorks</strong>' +
      '<p>Buy one, get one 50% off.</p>' +
    '</li>' +
    '<li><strong>Kobler Chiropractic</strong>' +
      '<p>No-cost initial consultation.</p>' +
      '<p>20% off all services.</p>' +
    '</li>' +
  '</ul>';

export function HawkFlockPartners() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const logo = useSlotGroup('hawkflockpartners.logo', logoDef);
  const editMode = useEditMode();
  const isEditing = Boolean(editMode?.isEditMode);
  const photo1 = useSlotGroup('hawkflockpartners.photo1', photo1Def);
  const photo2 = useSlotGroup('hawkflockpartners.photo2', photo2Def);
  const photo3 = useSlotGroup('hawkflockpartners.photo3', photo3Def);
  const photo4 = useSlotGroup('hawkflockpartners.photo4', photo4Def);
  const photo5 = useSlotGroup('hawkflockpartners.photo5', photo5Def);
  const photo6 = useSlotGroup('hawkflockpartners.photo6', photo6Def);
  const photoValues = [photo1, photo2, photo3, photo4, photo5, photo6];
  const showTile = (p: Record<string, string | null>) => isEditing || !!p.image;
  const renderPhoto = (p: Record<string, string | null>) =>
    p.image ? (
      <VibesitesImage
        url={p.image}
        alt={p.alt || 'Hawk Flock'}
        sizes="(min-width: 640px) 33vw, 50vw"
        className="w-full h-full object-cover"
      />
    ) : null;

  return (
    <section ref={sectionRef} className="reveal-fade-up hawkflock-partners">
      <div className="hawkflock-partners-container">
        {/* Hawk Flock logo lockup — slot group bundles image + editable alt. */}
        <div className="hawkflock-partners-header">
          <EditableSlotGroup
            groupPath="hawkflockpartners.logo"
            definition={logoDef}
            className="hawkflock-partners-logo-wrap"
          >
            {logo.image ? (
              <VibesitesImage
                url={logo.image}
                alt={logo.alt || 'Hawk Flock'}
                sizes="(min-width: 640px) 480px, 85vw"
                className="hawkflock-partners-logo"
              />
            ) : null}
          </EditableSlotGroup>
        </div>

        {/* Six-photo gallery — each tile has its own media picker. Empty
            tiles are hidden on the live site but stay clickable in the editor. */}
        {(isEditing || photoValues.some((p) => !!p.image)) && (
          <div className="hawkflock-partners-gallery">
            {showTile(photo1) && (
              <EditableSlotGroup groupPath="hawkflockpartners.photo1" definition={photo1Def} className="hawkflock-partners-photo">
                {renderPhoto(photo1)}
              </EditableSlotGroup>
            )}
            {showTile(photo2) && (
              <EditableSlotGroup groupPath="hawkflockpartners.photo2" definition={photo2Def} className="hawkflock-partners-photo">
                {renderPhoto(photo2)}
              </EditableSlotGroup>
            )}
            {showTile(photo3) && (
              <EditableSlotGroup groupPath="hawkflockpartners.photo3" definition={photo3Def} className="hawkflock-partners-photo">
                {renderPhoto(photo3)}
              </EditableSlotGroup>
            )}
            {showTile(photo4) && (
              <EditableSlotGroup groupPath="hawkflockpartners.photo4" definition={photo4Def} className="hawkflock-partners-photo">
                {renderPhoto(photo4)}
              </EditableSlotGroup>
            )}
            {showTile(photo5) && (
              <EditableSlotGroup groupPath="hawkflockpartners.photo5" definition={photo5Def} className="hawkflock-partners-photo">
                {renderPhoto(photo5)}
              </EditableSlotGroup>
            )}
            {showTile(photo6) && (
              <EditableSlotGroup groupPath="hawkflockpartners.photo6" definition={photo6Def} className="hawkflock-partners-photo">
                {renderPhoto(photo6)}
              </EditableSlotGroup>
            )}
          </div>
        )}

        {/* Row list — each <li> renders as "name | offer" at every viewport. */}
        <RichTextContent
          path="hawkflockpartners.partners_list"
          defaultValue={DEFAULT_PARTNERS_HTML}
          as="div"
          className="hawkflock-partners-list"
        />

        <RichTextContent
          path="hawkflockpartners.footnote"
          defaultValue='<p>Program partners and offers are subject to change. Ask our team at the leasing office for the most current list of participating businesses.</p>'
          as="p"
          className="hawkflock-partners-footnote"
        />
      </div>

      <style>{`
        /* Bottom padding is intentionally slim because PageBlurb (the next
           section on inner pages like /hawkflock) shares this same #F5F6F7
           background and brings its own top padding — too much here creates
           a dead gap between them. */
        .hawkflock-partners {
          padding: 3.5rem 0 1.25rem;
          background: #f5f6f7;
        }
        @media (min-width: 768px) {
          .hawkflock-partners { padding: 5rem 0 2rem; }
        }

        .hawkflock-partners-container {
          max-width: 64rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .hawkflock-partners-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .hawkflock-partners-container { padding: 0 2rem; } }

        .hawkflock-partners-header {
          text-align: center;
          max-width: 48rem;
          margin: 0 auto 2.5rem;
        }

        /* Hawk Flock logo lockup — centered. */
        .hawkflock-partners-logo-wrap {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0 auto;
          max-width: 480px;
        }
        .hawkflock-partners-logo {
          display: block;
          width: 100%;
          height: auto;
          max-height: 180px;
          object-fit: contain;
        }
        @media (min-width: 768px) {
          .hawkflock-partners-logo { max-height: 220px; }
        }

        /* Six-photo gallery — 2 cols on mobile → 3 cols on tablet+ so a full
           set of 6 lays out as either 2×3 or 3×2. Each tile is a 4:3 image. */
        .hawkflock-partners-gallery {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
          margin: 0 auto 2.5rem;
        }
        @media (min-width: 640px) {
          .hawkflock-partners-gallery {
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem;
          }
        }
        .hawkflock-partners-photo {
          display: block;
          position: relative;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          border-radius: var(--vibe-radius-md, 8px);
          background: #e6e7ea;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }
        .hawkflock-partners-photo :where(img) {
          transition: transform 0.4s ease;
        }
        .hawkflock-partners-photo:hover :where(img) {
          transform: scale(1.04);
        }

        /* Row list — one partner per row, name on the left, offer on the right.
           Two-column grid at every viewport, so content always sits beside the
           title (never underneath). Each row can contain many paragraphs
           (e.g. AdventHealth has 3; Kobler has 2) — the explicit
           grid-column:2 on every paragraph prevents CSS grid's auto-flow from
           zig-zagging extra paragraphs back under the title. */
        .hawkflock-partners-list :where(ul) {
          list-style: none;
          margin: 0;
          padding: 0;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
        }
        .hawkflock-partners-list :where(li) {
          display: grid;
          grid-template-columns: 7.5rem 1fr;
          column-gap: 1rem;
          row-gap: 0.4rem;
          padding: 1rem;
          border-bottom: 1px solid #eef0f2;
          align-items: start;
        }
        .hawkflock-partners-list :where(li):last-child {
          border-bottom: none;
        }
        @media (min-width: 480px) {
          .hawkflock-partners-list :where(li) {
            grid-template-columns: 10rem 1fr;
            column-gap: 1.5rem;
            padding: 1.25rem 1.5rem;
          }
        }
        @media (min-width: 768px) {
          .hawkflock-partners-list :where(li) {
            grid-template-columns: minmax(12rem, 14rem) 1fr;
            column-gap: 2rem;
            padding: 1.5rem 2rem;
          }
        }

        /* Title: pinned to column 1, row 1. */
        .hawkflock-partners-list :where(li) > :where(strong) {
          grid-column: 1;
          grid-row: 1;
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 700;
          line-height: 1.2;
          color: #000000;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          overflow-wrap: break-word;
          word-break: break-word;
          margin: 0;
        }
        @media (min-width: 640px) {
          .hawkflock-partners-list :where(li) > :where(strong) {
            font-size: 1.05rem;
          }
        }
        @media (min-width: 768px) {
          .hawkflock-partners-list :where(li) > :where(strong) {
            font-size: 1.1rem;
          }
        }

        /* All paragraphs forced into column 2 so they stack vertically beside
           the title regardless of how many there are. */
        .hawkflock-partners-list :where(li) > :where(p) {
          grid-column: 2;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.55;
          color: var(--vibe-color-text);
          margin: 0;
        }
        @media (min-width: 640px) {
          .hawkflock-partners-list :where(li) > :where(p) {
            font-size: 0.95rem;
            line-height: 1.6;
          }
        }
        .hawkflock-partners-list :where(li) :where(em) {
          color: var(--vibe-color-text-muted, #6b7280);
          font-size: 0.82rem;
        }
        .hawkflock-partners-list :where(li) :where(a) {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .hawkflock-partners-footnote {
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted, #6b7280);
          margin: 1.75rem auto 0;
          max-width: 42rem;
          font-style: italic;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ──────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'HawkFlockPartners',
  displayName: 'HawkFlock — Partners',
  tokens: {
    'hawkflockpartners.partners_list': {
      label: 'Participating Partners',
      type: 'richtext',
      description:
        'Each list item becomes one partner row. Put the business name in bold on the first line and the offer in paragraphs beneath it. Add, remove, or re-order items as the program grows.',
      defaultValue:
        '<ul>' +
          '<li><strong>AdventHealth Sports Park at Bluhawk</strong>' +
            '<p>Complimentary Public Ice Skating with shoe rental (valid for up to 4 guests).<br>' +
            '<em>*Check the public calendar first for availability at bluhawksports.com/public-ice-skating</em></p>' +
            '<p>Complimentary Open Fieldhouse access: play pickleball, soccer, volleyball &amp; basketball.<br>' +
            '<em>*Check the fieldhouse calendar first for availability at bluhawksports.com/fieldhouse</em></p>' +
            '<p>Spend $25 in arcade credits, receive $25 free arcade credits.</p>' +
          '</li>' +
          '<li><strong>C26 Hub</strong>' +
            '<p><a href="/c26">Click here for a full list of resident perks.</a></p>' +
          '</li>' +
          '<li><strong>LevelUp</strong>' +
            '<p>50% off gaming when you present your Hawk Flock Card.<br>' +
            '<em>*Restrictions may apply during other event promotions, tournaments, and events.</em></p>' +
          '</li>' +
          '<li><strong>Scotts Diamond Designs</strong>' +
            '<p>Clean and inspect rings purchased from any store for FREE.</p>' +
          '</li>' +
          '<li><strong>Brutus Barkery + Bath</strong>' +
            '<p>(Formerly Three Dog Bakery) — 10% off store purchases.<br>' +
            '<em>*Excludes food.</em></p>' +
          '</li>' +
          '<li><strong>Jinya</strong>' +
            '<p>10% discount on food and non-alcoholic beverages for residents. T&amp;C may apply.<br>' +
            '<em>*Must present valid Hawk Flock VIP card.<br>' +
            '*Dine-in only.<br>' +
            '*Not applicable for alcoholic beverages.<br>' +
            '*Not applicable during Happy Hour.<br>' +
            '*Cannot be combined with any other promotions.</em></p>' +
          '</li>' +
          '<li><strong>SaladWorks</strong>' +
            '<p>Buy one, get one 50% off.</p>' +
          '</li>' +
          '<li><strong>Kobler Chiropractic</strong>' +
            '<p>No-cost initial consultation.</p>' +
            '<p>20% off all services.</p>' +
          '</li>' +
        '</ul>',
    },
    'hawkflockpartners.footnote': {
      label: 'Footnote',
      type: 'richtext',
      defaultValue:
        '<p>Program partners and offers are subject to change. Ask our team at the leasing office for the most current list of participating businesses.</p>',
    },
  },
  slotGroups: {
    'hawkflockpartners.logo': logoDef,
    'hawkflockpartners.photo1': photo1Def,
    'hawkflockpartners.photo2': photo2Def,
    'hawkflockpartners.photo3': photo3Def,
    'hawkflockpartners.photo4': photo4Def,
    'hawkflockpartners.photo5': photo5Def,
    'hawkflockpartners.photo6': photo6Def,
  },
};

(HawkFlockPartners as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HawkFlockPartners;
